/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file currencySlice.js
 * @description Redux slice for managing currency related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the currency slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (currencies = null) => ({
  currencies,
  error: null,
  loading: false,
});

/**
 * @function getCurrencies
 * @description Async thunk for get currencies operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getCurrencies = createAsyncThunk("currencies/loadCurrencies", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/currencies?originId=${originId}&lineId=${lineId}`);

    return {
      currencies: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain currency details", // serializable (err.response.data)
      name: "Currency List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant currencySlice
 * @description Redux slice for currency management
 */
const currencySlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getCurrencies.pending, (state) => {
        state.currencies = null;
        state.error = null;
        state.loading = true;
      })
      .addCase(getCurrencies.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getCurrencies.fulfilled, (state, action) => {
        state.currencies = action.payload.currencies;
        state.error = null;
        state.loading = false;
      });
  },
  initialState: getInitialState(),
  name: "currency",
  reducers: {},
});
const { reducer } = currencySlice;
export default reducer;
